<template>
  <aside class="erp-sider" :class="{ collapsed }">
    <div class="erp-logo">
      <img src="/logo.png" alt="经营核算平台" class="erp-logo-img" />
      <span v-show="!collapsed" class="erp-logo-text">经营核算平台</span>
      <button
        type="button"
        class="erp-collapse-toggle"
        :title="collapsed ? '展开菜单' : '收起菜单'"
        @click="toggleCollapsed"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          :class="{ 'is-flipped': collapsed }"
        >
          <path
            d="M10.5 3.5L6 8l4.5 4.5"
            stroke="currentColor"
            stroke-width="1.5"
            fill="none"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>

    <nav class="erp-nav">
      <template v-for="mod in sidebarModules">
        <!-- 经营驾驶舱 -->
        <template v-if="mod.standalone">
          <div
            v-for="m in mod.menus"
            :key="m.key"
            class="erp-nav-item-wrap"
            @mouseenter="onItemEnter(mod, $event)"
            @mouseleave="onItemLeave"
          >
            <router-link
              :to="m.path"
              class="erp-nav-dashboard"
              :class="{ active: activeKey === m.key }"
              @click="hideFlyout"
            >
              <span class="end-icon end-icon-dashboard">
                <sidebar-icon :name="m.iconKey" size="md" tone="white" />
              </span>
              <span v-show="!collapsed" class="end-label">{{ m.text }}</span>
              <span v-show="!collapsed && activeKey === m.key" class="end-arrow">›</span>
            </router-link>
          </div>
        </template>

        <!-- 分组模块 -->
        <div
          v-else
          :key="mod.code"
          class="erp-module erp-nav-item-wrap"
          :class="`theme-${mod.theme}`"
          @mouseenter="onItemEnter(mod, $event)"
          @mouseleave="onItemLeave"
        >
          <button
            type="button"
            class="erp-module-head"
            :class="{ open: isOpen(mod.code), 'has-active': hasActiveChild(mod) }"
            @click="toggleModule(mod.code)"
          >
            <span class="end-icon">
              <sidebar-icon :name="mod.iconKey" size="md" tone="white" />
            </span>
            <span v-show="!collapsed" class="end-label">{{ mod.name }}</span>
            <span v-show="!collapsed" class="end-chevron">
              <svg width="12" height="12" viewBox="0 0 12 12"><path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/></svg>
            </span>
          </button>
          <div v-show="!collapsed && isOpen(mod.code)" class="erp-module-body">
            <router-link
              v-for="m in mod.menus"
              :key="m.key"
              :to="m.path"
              class="erp-sub-link"
              :class="{ active: activeKey === m.key }"
            >
              <span class="erp-sub-ico">
                <sidebar-icon :name="m.iconKey" size="sm" tone="sub" />
              </span>
              <span class="erp-sub-text">{{ m.text }}</span>
            </router-link>
          </div>
        </div>
      </template>
    </nav>

    <!-- 收起态：悬浮二级菜单 -->
    <div
      v-if="collapsed && flyoutModule"
      class="erp-flyout"
      :style="{ top: flyoutTop + 'px' }"
      @mouseenter="onFlyoutEnter"
      @mouseleave="onFlyoutLeave"
    >
      <div v-if="!flyoutModule.standalone" class="erp-flyout-head">{{ flyoutModule.name }}</div>
      <router-link
        v-for="m in flyoutModule.menus"
        :key="m.key"
        :to="m.path"
        class="erp-flyout-link"
        :class="{ active: activeKey === m.key }"
        @click="hideFlyout"
      >
        <span class="erp-flyout-ico">
          <sidebar-icon :name="m.iconKey" size="sm" tone="sub" />
        </span>
        <span>{{ m.text }}</span>
      </router-link>
    </div>
  </aside>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { buildSidebarModules } from '@/utils/sidebarMenu'
import SidebarIcon from '@/components/SidebarIcon.vue'

export default {
  name: 'AppSidebar',
  components: { SidebarIcon },
  props: {
    activeKey: { type: String, default: '' }
  },
  data() {
    return {
      collapsed: false,
      openModules: {},
      flyoutModule: null,
      flyoutTop: 0,
      flyoutTimer: null
    }
  },
  computed: {
    ...mapState(['menuModules']),
    ...mapGetters(['visibleMenus']),
    sidebarModules() {
      return buildSidebarModules(this.menuModules, this.visibleMenus)
    }
  },
  watch: {
    sidebarModules: {
      immediate: true,
      handler(mods) {
        mods.forEach((mod) => {
          if (!mod.standalone && this.openModules[mod.code] === undefined) {
            this.$set(this.openModules, mod.code, true)
          }
        })
      }
    },
    activeKey: {
      immediate: true,
      handler(key) {
        if (!key) return
        this.sidebarModules.forEach((mod) => {
          if (!mod.standalone && mod.menus.some((m) => m.key === key)) {
            this.$set(this.openModules, mod.code, true)
          }
        })
      }
    },
    collapsed(val) {
      if (val) this.hideFlyout()
    }
  },
  beforeDestroy() {
    this.clearFlyoutTimer()
  },
  methods: {
    toggleCollapsed() {
      this.collapsed = !this.collapsed
    },
    isOpen(code) {
      return this.openModules[code] !== false
    },
    hasActiveChild(mod) {
      return mod.menus.some((m) => m.key === this.activeKey)
    },
    toggleModule(code) {
      if (this.collapsed) return
      this.$set(this.openModules, code, !this.isOpen(code))
    },
    clearFlyoutTimer() {
      if (this.flyoutTimer) {
        clearTimeout(this.flyoutTimer)
        this.flyoutTimer = null
      }
    },
    onItemEnter(mod, e) {
      if (!this.collapsed) return
      this.clearFlyoutTimer()
      this.flyoutModule = mod
      const rect = e.currentTarget.getBoundingClientRect()
      const maxTop = window.innerHeight - 280
      this.flyoutTop = Math.min(Math.max(rect.top, 8), maxTop)
    },
    onItemLeave() {
      if (!this.collapsed) return
      this.clearFlyoutTimer()
      this.flyoutTimer = setTimeout(() => {
        this.flyoutModule = null
      }, 120)
    },
    onFlyoutEnter() {
      this.clearFlyoutTimer()
    },
    onFlyoutLeave() {
      this.hideFlyout()
    },
    hideFlyout() {
      this.clearFlyoutTimer()
      this.flyoutModule = null
    }
  }
}
</script>
